import { Grid, Typography } from "@material-ui/core";
import { EmptyState, InfoCard, Progress } from "@backstage/core-components";
import { useEntity } from "@backstage/plugin-catalog-react";
import { ResourceUtilization, useKubernetesObjects } from "@backstage/plugin-kubernetes-react";
import { aggregatePodMetrics, formatMebibytes, formatMillicores } from "./runtimeResources";

type ResourceSummaryProps = {
  title: string;
  usage: number;
  total: number;
  totalFormatted: string;
  emptyDescription: string;
};

const ResourceSummary = ({
  title,
  usage,
  total,
  totalFormatted,
  emptyDescription,
}: ResourceSummaryProps) => {
  if (total <= 0) {
    return (
      <Typography variant="body2" color="textSecondary">
        {emptyDescription}
      </Typography>
    );
  }

  return (
    <ResourceUtilization
      title={title}
      usage={usage}
      total={total}
      totalFormatted={totalFormatted}
    />
  );
};

export const KubernetesResourcesCard = () => {
  const { entity } = useEntity();
  const { kubernetesObjects, loading, error } = useKubernetesObjects(entity);
  const podMetrics = kubernetesObjects?.items.flatMap(item => item.podMetrics) ?? [];
  const summary = aggregatePodMetrics(podMetrics);

  return (
    <InfoCard title="Runtime Resources">
      {loading ? <Progress /> : null}
      {error ? (
        <EmptyState
          title="Runtime resource metrics are unavailable"
          missing="info"
          description={error}
        />
      ) : null}
      {!loading && !error && summary.podCount === 0 ? (
        <EmptyState
          title="No runtime resource metrics"
          missing="data"
          description="Kubernetes pod metrics are not available for this entity yet."
        />
      ) : null}
      {!loading && !error && summary.podCount > 0 ? (
        <Grid container spacing={3}>
          <Grid item xs={12}>
            <Typography variant="body2" color="textSecondary">
              Pods with metrics: {summary.podCount}
            </Typography>
            <Typography variant="body2" color="textSecondary">
              Current CPU: {formatMillicores(summary.cpu.currentUsage)}
            </Typography>
            <Typography variant="body2" color="textSecondary">
              Current Memory: {formatMebibytes(summary.memory.currentUsage)}
            </Typography>
          </Grid>
          <Grid item md={6} xs={12}>
            <ResourceSummary
              title="CPU requests"
              usage={summary.cpu.currentUsage}
              total={summary.cpu.requestTotal}
              totalFormatted={formatMillicores(summary.cpu.requestTotal)}
              emptyDescription="CPU requests are not configured for these pods."
            />
            <ResourceSummary
              title="CPU limits"
              usage={summary.cpu.currentUsage}
              total={summary.cpu.limitTotal}
              totalFormatted={formatMillicores(summary.cpu.limitTotal)}
              emptyDescription="CPU limits are not configured for these pods."
            />
          </Grid>
          <Grid item md={6} xs={12}>
            <ResourceSummary
              title="Memory requests"
              usage={summary.memory.currentUsage}
              total={summary.memory.requestTotal}
              totalFormatted={formatMebibytes(summary.memory.requestTotal)}
              emptyDescription="Memory requests are not configured for these pods."
            />
            <ResourceSummary
              title="Memory limits"
              usage={summary.memory.currentUsage}
              total={summary.memory.limitTotal}
              totalFormatted={formatMebibytes(summary.memory.limitTotal)}
              emptyDescription="Memory limits are not configured for these pods."
            />
          </Grid>
        </Grid>
      ) : null}
    </InfoCard>
  );
};
